import { padStart } from 'lodash';

class BitArrayBacking {
  readonly length: number;
  private readonly wordArray: Uint32Array;

  constructor(size: number, hex?: string) {
    this.length = size;
    const buffer = new ArrayBuffer(Math.ceil(this.length / 32) * 4);
    this.wordArray = new Uint32Array(buffer);

    if (hex) {
      let h = hex.replace(/^0x/i, '');
      if (h.length * 4 > this.length) {
        throw new Error('Hex value is too large for this bit array.');
      }
      while (h.length * 4 < this.length) {
        h = '0' + h;
      }
      for (let i = 0; i < h.length / 8; i++) {
        const slice = h.slice(i * 8, i * 8 + 8);
        this.wordArray[i] = parseInt(slice, 16);
      }
    }
  }

  set(index: number, value: boolean): void {
    if (index > this.length - 1) {
      throw new Error(`Index too large: ${index} ${this.length}`);
    }
    const wordOffset = Math.floor(index / 32);
    const bitOffset = index - wordOffset * 32;
    if (value) {
      this.wordArray[wordOffset] |= 1 << bitOffset;
    } else {
      this.wordArray[wordOffset] &= ~(1 << bitOffset);
    }
  }

  get(index: number): boolean {
    if (index > this.length - 1) {
      throw new Error('Index too large.');
    }
    const wordOffset = Math.floor(index / 32);
    const bitOffset = index - wordOffset * 32;
    return !!(this.wordArray[wordOffset] & (1 << bitOffset));
  }

  toHexString(): string {
    const result: string[] = [];
    for (let i = 0; i < this.wordArray.length; i += 1) {
      result.push(('00000000' + (this.wordArray[i] >>> 0).toString(16)).slice(-8));
    }
    return result.join('');
  }
}

function reorderHex(hex: string): string {
  const wordCount = Math.ceil(hex.length / 8);

  const words: string[] = [];
  for (let i = 0; i < wordCount; i++) {
    words.push(hex.substring(i * 8, i * 8 + 8));
  }
  words.reverse();

  return words.join('');
}

export class BitSet<T> {
  private readonly array: BitArrayBacking;

  constructor(size: number = 64, hex?: string) {
    let normalizedHex: string | undefined = hex;
    if (hex) {
      normalizedHex = reorderHex(padStart(hex, size / 4, '0'));
    }

    this.array = new BitArrayBacking(size, normalizedHex);
  }

  set(order: T, value: boolean): void {
    this.array.set(order as unknown as number, value);
  }

  has(order: T): boolean {
    return this.array.get(order as unknown as number);
  }

  toString(): string {
    return reorderHex(this.array.toHexString());
  }
}
